/*!
 * Loftsails Configure Modal — Full-page quiver builder
 * Version: 1.2.4
 */

(function() {
  'use strict';

  class LoftsailsConfigureModal {
    constructor(root) {
      this.ready = false;
      this.root = root;
      this.overlay = root.querySelector('[data-lscm-overlay]');
      this.heroEl = root.querySelector('[data-lscm-hero]');
      this.confirmBtn = root.querySelector('[data-lscm-confirm]');
      this.constructionButtons = root.querySelectorAll('[data-construction]');
      this.optionButtons = root.querySelectorAll('[data-lscm-option]');
      this.selectedOptionEl = root.querySelector('[data-lscm-selected-option]');
      this.colorButtons = root.querySelectorAll('[data-color]');
      this.sizeButtons = root.querySelectorAll('[data-size]');
      this.bisEntries = root.querySelectorAll('[data-lscm-bis-entry]');
      this.selectionEl = root.querySelector('[data-lscm-selection]');
      this.countEl = root.querySelector('[data-lscm-count]');
      this.selectedConstructionEl = root.querySelector('[data-lscm-selected-construction]');
      this.selectedColorEl = root.querySelector('[data-lscm-selected-color]');
      this.priceEl = root.querySelector('[data-lscm-price]');
      this.compareEl = root.querySelector('[data-lscm-compare]');

      const dataEl = root.querySelector('[data-lscm-data]');
      if (!dataEl) return;

      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('LSCM: Failed to parse data', e);
        return;
      }

      if (!Array.isArray(this.data.variants) || this.data.variants.length === 0) {
        console.error('LSCM: No variants in modal data');
        return;
      }

      this.strings = this.data.strings || {};

      this.combinedOptions = Boolean(this.data.combinedOptions);
      this.race2027Pdp = Boolean(this.data.race2027Pdp);
      this.showConstruction = Boolean(this.data.showConstruction);
      this.showColor = Boolean(this.data.showColor);
      this.selectedConstruction = this.data.currentProduct.construction || '';
      this.selectedColor = this.data.currentProduct.color || '';
      const currentVariant = this.data.variants.find((variant) => (
        String(variant.id) === String(this.data.currentProduct.id) ||
        String(variant.variantId) === String(this.data.currentProduct.variantId)
      ));
      this.selectedSizes = currentVariant ? [currentVariant.size] : [];
      this.maxSailsAllowed = this.data.maxSailsAllowed || 5;
      this.lastFocus = null;
      this.keydownHandler = (e) => this.handleKeydown(e);
      this.backdropClickHandler = (event) => {
        if (event.target === this.overlay && this.isOpen()) this.close();
      };

      if (this.combinedOptions) {
        this.sizeButtons = Array.from(this.sizeButtons).sort((a, b) => parseFloat(a.dataset.size) - parseFloat(b.dataset.size));
        this.sizeButtons.forEach(button => button.parentElement.appendChild(button));
      }

      this.bindEvents();
      this.syncActivePills();
      this.reconcileSelectedSizes(false);
      this.syncConstructionPills();
      this.syncSizePills();
      this.syncColorPills();
      this.updateConfirmButton();
      this.updatePriceDisplay();
      this.updateSelectionDisplay();
      this.updateVisuals();

      this.ready = true;
      root.removeAttribute('hidden');
    }

    translate(key, fallback) {
      return this.strings[key] || fallback;
    }

    // Mirrors the PDP size picker: on the 2027 race PDPs an existing but
    // unavailable sail is announced as coming soon, never as sold out.
    isComingSoonVariant(variant) {
      return Boolean(this.race2027Pdp && variant && variant.available === false);
    }

    unavailableLabel(comingSoon, fallback = 'Sold out') {
      return comingSoon
        ? this.translate('comingSoon', 'Coming soon')
        : this.translate('soldOut', fallback);
    }

    withCount(key, fallback, count) {
      return this.translate(key, fallback).replace('__COUNT__', count);
    }

    bindEvents() {
      this.root.querySelectorAll('[data-lscm-close]').forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });

      document.addEventListener('keydown', this.keydownHandler);
      this.overlay?.addEventListener('click', this.backdropClickHandler);

      this.optionButtons.forEach(btn => {
        btn.addEventListener('click', () => this.selectOption(btn.dataset.optionConstruction || '', btn.dataset.optionColor || ''));
      });

      this.colorButtons.forEach(btn => {
        btn.addEventListener('click', () => this.selectColor(btn.dataset.color));
      });

      this.constructionButtons.forEach(btn => {
        btn.addEventListener('click', () => this.selectConstruction(btn.dataset.construction || ''));
      });

      this.sizeButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          if (!btn.disabled) {
            this.toggleSize(btn.dataset.size);
          }
        });
      });

      if (this.confirmBtn) {
        this.root.addEventListener('click', (e) => {
          const confirm = e.target.closest('[data-lscm-confirm]');
          if (!confirm || confirm.disabled) return;
          e.preventDefault();
          e.stopPropagation();
          this.confirmSelection();
        });
      }
    }

    open() {
      if (!this.overlay) return false;

      this.lastFocus = document.activeElement;
      this.overlay.classList.add('is-open');
      this.overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      setTimeout(() => {
        if (!this.isOpen()) return;
        const focusable = this.getFocusableElements();
        const firstPill = focusable.find(element => element.matches('.lscm__pill.is-active, .lscm__size-pill.is-selected'));
        (firstPill || focusable[0])?.focus();
      }, 100);

      return true;
    }

    getFocusableElements() {
      return Array.from(this.overlay.querySelectorAll('a[href], button, input, select, textarea, [tabindex]'))
        .filter(element => element.tabIndex >= 0 && !element.matches(':disabled') &&
          !element.closest('[hidden], [inert]') && element.getClientRects().length > 0 &&
          getComputedStyle(element).visibility !== 'hidden');
    }

    handleKeydown(event) {
      if (!this.isOpen()) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = this.getFocusableElements();
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first) {
        event.preventDefault();
        return;
      }
      const active = document.activeElement;
      if (!focusable.includes(active) || (event.shiftKey ? active === first : active === last)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    }

    close() {
      if (!this.overlay) return;

      // Move focus out of the overlay BEFORE hiding it from assistive tech,
      // otherwise aria-hidden is blocked/rejected while a descendant retains
      // focus. Mirrors the variant picker's closeModal() ordering.
      const previous = this.lastFocus;
      if (previous && previous.isConnected && typeof previous.focus === 'function' && !previous.disabled) {
        previous.focus({ preventScroll: true });
      } else if (document.activeElement && this.overlay.contains(document.activeElement)) {
        document.activeElement.blur();
      }

      this.overlay.classList.remove('is-open');
      this.overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    destroy() {
      document.removeEventListener('keydown', this.keydownHandler);
      this.overlay?.removeEventListener('click', this.backdropClickHandler);
      this.close();
    }

    isOpen() {
      return this.overlay && this.overlay.classList.contains('is-open');
    }

    variantMatchesSelection(variant, selection = {}) {
      const size = selection.size !== undefined ? selection.size : null;
      const construction = selection.construction !== undefined ? selection.construction : this.selectedConstruction;
      const color = selection.color !== undefined ? selection.color : this.selectedColor;

      if (size !== null && variant.size !== size) return false;
      if ((this.combinedOptions || this.showConstruction) && (variant.construction || '') !== (construction || '')) return false;
      if ((this.combinedOptions || this.showColor) && (variant.color || '') !== (color || '')) return false;

      return true;
    }

    findVariant(size, construction = this.selectedConstruction, color = this.selectedColor) {
      return this.data.variants.find(v => this.variantMatchesSelection(v, { size, construction, color }));
    }

    getSelectedVariants() {
      return this.selectedSizes
        .map(size => this.findVariant(size))
        .filter(Boolean);
    }

    syncActivePills() {
      this.optionButtons.forEach(btn => {
        const construction = btn.dataset.optionConstruction || '';
        const color = btn.dataset.optionColor || '';
        const active = construction === this.selectedConstruction && color === this.selectedColor;
        const variants = this.data.variants.filter(v => (v.construction || '') === construction && (v.color || '') === color);
        const unavailable = variants.length > 0 && variants.every(v => !v.available);
        const comingSoon = unavailable && variants.every(v => this.isComingSoonVariant(v));
        btn.classList.toggle('is-active', active);
        btn.classList.toggle('is-sold-out', unavailable && !comingSoon);
        btn.classList.toggle('is-coming-soon', comingSoon);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        btn.setAttribute('aria-label', `${this.formatOption(construction, color)}${unavailable ? ', ' + this.unavailableLabel(comingSoon) : ''}`);
      });
      this.constructionButtons.forEach(btn => {
        const active = (btn.dataset.construction || '') === this.selectedConstruction;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      this.colorButtons.forEach(btn => {
        const active = (btn.dataset.color || '') === this.selectedColor;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    reconcileDependentAxes(changedAxis) {
      if (changedAxis === 'construction' && this.showColor) {
        const hasCurrentColor = this.data.variants.some(v => (
          (v.construction || '') === this.selectedConstruction &&
          (v.color || '') === this.selectedColor
        ));
        if (!hasCurrentColor) {
          const fallback = this.data.variants.find(v => (
            (v.construction || '') === this.selectedConstruction &&
            v.available
          )) || this.data.variants.find(v => (v.construction || '') === this.selectedConstruction);
          if (fallback) this.selectedColor = fallback.color || '';
        }
      }

      if (changedAxis === 'color' && this.showConstruction) {
        const hasCurrentConstruction = this.data.variants.some(v => (
          (v.color || '') === this.selectedColor &&
          (v.construction || '') === this.selectedConstruction
        ));
        if (!hasCurrentConstruction) {
          const fallback = this.data.variants.find(v => (
            (v.color || '') === this.selectedColor &&
            v.available
          )) || this.data.variants.find(v => (v.color || '') === this.selectedColor);
          if (fallback) this.selectedConstruction = fallback.construction || '';
        }
      }
    }

    formatOption(construction, color) {
      if (color.toLowerCase() === 'green') return this.translate('ltdGreen', 'LTD Green');
      return [construction, color].filter(Boolean).join(' ');
    }

    selectOption(construction, color) {
      this.selectedConstruction = construction;
      this.selectedColor = color;
      this.reconcileSelectedSizes(false);
      this.syncActivePills();
      this.syncSizePills();
      this.updateConfirmButton();
      this.updatePriceDisplay();
      this.updateSelectionDisplay();
      this.updateVisuals();
    }

    selectConstruction(construction) {
      this.selectedConstruction = construction || '';
      this.reconcileDependentAxes('construction');
      this.syncActivePills();

      this.reconcileSelectedSizes();
      this.syncConstructionPills();
      this.syncColorPills();
      this.syncSizePills();
      this.updateConfirmButton();
      this.updatePriceDisplay();
      this.updateSelectionDisplay();
      this.updateVisuals();
    }

    selectColor(color) {
      this.selectedColor = color || '';
      this.reconcileDependentAxes('color');
      this.syncActivePills();

      this.reconcileSelectedSizes();
      this.syncConstructionPills();
      this.syncSizePills();
      this.syncColorPills();
      this.updateConfirmButton();
      this.updatePriceDisplay();
      this.updateSelectionDisplay();
      this.updateVisuals();
    }

    reconcileSelectedSizes(allowFallback = true) {
      this.selectedSizes = this.selectedSizes.filter(size => {
        const variant = this.findVariant(size);
        return Boolean(variant);
      });

      if (allowFallback && !this.combinedOptions && this.selectedSizes.length === 0) {
        const fallback = this.data.variants.find(v => this.variantMatchesSelection(v) && v.available);
        if (fallback) this.selectedSizes = [fallback.size];
      }
    }

    syncConstructionPills() {
      this.constructionButtons.forEach(btn => {
        const construction = btn.dataset.construction || '';
        const variants = this.data.variants.filter(v => {
          if ((v.construction || '') !== construction) return false;
          if (this.showColor && (v.color || '') !== this.selectedColor) return false;
          return true;
        });
        const availableCount = variants.filter(v => v.available).length;
        const label = this.formatConstruction(construction);

        const comingSoon = variants.length > 0 && availableCount === 0 && variants.every(v => this.isComingSoonVariant(v));

        btn.classList.toggle('is-unavailable', variants.length === 0);
        btn.classList.toggle('is-sold-out', variants.length > 0 && availableCount === 0 && !comingSoon);
        btn.classList.toggle('is-coming-soon', comingSoon);

        if (variants.length === 0) {
          btn.setAttribute('aria-label', `${label}, ${this.translate('notAvailable', 'not available')}`);
        } else if (availableCount === 0) {
          btn.setAttribute('aria-label', `${label}, ${this.unavailableLabel(comingSoon, 'sold out')}`);
        } else {
          btn.removeAttribute('aria-label');
        }
      });
    }

    syncColorPills() {
      this.colorButtons.forEach(btn => {
        const color = btn.dataset.color || '';
        const variants = this.data.variants.filter(v => {
          if ((v.color || '') !== color) return false;
          if (this.showConstruction && (v.construction || '') !== this.selectedConstruction) return false;
          return true;
        });
        const availableCount = variants.filter(v => v.available).length;
        const label = this.formatColor(color);

        const comingSoon = variants.length > 0 && availableCount === 0 && variants.every(v => this.isComingSoonVariant(v));

        btn.classList.toggle('is-unavailable', variants.length === 0);
        btn.classList.toggle('is-sold-out', variants.length > 0 && availableCount === 0 && !comingSoon);
        btn.classList.toggle('is-coming-soon', comingSoon);

        if (variants.length === 0) {
          btn.setAttribute('aria-label', `${label}, ${this.translate('notAvailable', 'not available')}`);
        } else if (availableCount === 0) {
          btn.setAttribute('aria-label', `${label}, ${this.unavailableLabel(comingSoon, 'sold out')}`);
        } else {
          btn.removeAttribute('aria-label');
        }
      });
    }

    toggleSize(size) {
      const variant = this.findVariant(size);
      if (!variant) return;

      if (!variant.available) {
        this.selectedSizes = [size];
        this.syncSizePills();
        this.updateConfirmButton();
        this.updatePriceDisplay();
        this.updateSelectionDisplay();
        this.updateVisuals();
        return;
      }

      this.selectedSizes = this.selectedSizes.filter(selectedSize => {
        const selectedVariant = this.findVariant(selectedSize);
        return selectedVariant?.available;
      });

      const index = this.selectedSizes.indexOf(size);

      if (index >= 0) {
        if (this.selectedSizes.length === 1) return;
        this.selectedSizes.splice(index, 1);
      } else {
        if (this.selectedSizes.length >= this.maxSailsAllowed) {
          this.showToast(this.withCount('maximumSails', 'Maximum __COUNT__ sails per quiver', this.maxSailsAllowed));
          return;
        }
        this.selectedSizes.push(size);
        this.selectedSizes.sort((a, b) => parseFloat(a) - parseFloat(b));
      }

      this.syncSizePills();
      this.updateConfirmButton();
      this.updatePriceDisplay();
      this.updateSelectionDisplay();
      this.updateVisuals();
    }

    syncSizePills() {
      const atLimit = this.selectedSizes.length >= this.maxSailsAllowed;

      this.sizeButtons.forEach(btn => {
        const size = btn.dataset.size;
        const variant = this.findVariant(size);
        const priceEl = btn.querySelector('.lscm__size-meta');
        const available = variant && variant.available;
        const comingSoon = this.isComingSoonVariant(variant);
        const selected = this.selectedSizes.includes(size);

        // A size with no product for the current model/color does not exist —
        // hide it instead of showing a dead "Not available" pill.
        btn.hidden = !variant;
        btn.classList.toggle('is-disabled', !variant);
        btn.classList.toggle('is-sold-out', Boolean(variant) && !available && !comingSoon);
        btn.classList.toggle('is-coming-soon', comingSoon);
        btn.classList.toggle('is-unavailable', !variant);
        btn.classList.toggle('is-selected', selected);
        btn.classList.toggle('is-at-limit', !selected && atLimit && available);
        btn.disabled = !variant;
        btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
        btn.setAttribute('aria-disabled', variant ? (this.combinedOptions || available ? 'false' : 'true') : 'true');

        if (priceEl) {
          if (!variant) {
            priceEl.className = 'lscm__size-meta lscm__size-status';
            priceEl.textContent = this.translate('notAvailable', 'Not available');
            btn.setAttribute('aria-label', `${size}m², ${this.translate('notAvailable', 'not available')}`);
          } else if (available) {
            btn.removeAttribute('aria-label');
            priceEl.className = 'lscm__size-meta lscm__size-price';
            priceEl.textContent = this.formatMoney(variant.price);
          } else {
            priceEl.className = 'lscm__size-meta lscm__size-status';
            priceEl.textContent = this.unavailableLabel(comingSoon);
            btn.setAttribute('aria-label', `${size}m², ${this.unavailableLabel(comingSoon, 'sold out')}`);
          }
        }
      });

      this.bisEntries.forEach(entry => {
        const variant = this.selectedSizes.length === 1 ? this.findVariant(this.selectedSizes[0]) : null;
        const show = Boolean(variant) && !variant.available && (this.combinedOptions
          ? entry.dataset.lscmBisVariantId === String(variant.variantId)
          : entry.dataset.lscmBisSize === this.selectedSizes[0]);
        entry.hidden = !show;
      });
    }

    updateVisuals() {
      // Hero is a dedicated mapped image set in Liquid — it stays static.
    }

    updateSelectionDisplay() {
      const count = this.selectedSizes.length;
      const sizesLabel = this.selectedSizes.map(size => `${size}m²`).join(', ');
      if (this.selectedOptionEl) {
        this.selectedOptionEl.textContent = this.formatOption(this.selectedConstruction, this.selectedColor);
      }

      if (this.selectionEl) {
        this.selectionEl.textContent = sizesLabel;
      }

      if (this.countEl) {
        this.countEl.textContent = count === 1
          ? this.translate('oneSail', '1 sail')
          : this.withCount('sailsCount', '__COUNT__ sails', count);
      }

      if (this.selectedColorEl) {
        this.selectedColorEl.textContent = this.formatColor(this.selectedColor);
      }

      if (this.selectedConstructionEl) {
        this.selectedConstructionEl.textContent = this.formatConstruction(this.selectedConstruction);
      }
    }

    hasUnavailableSelection() {
      return this.combinedOptions && this.selectedSizes.some(size => !this.findVariant(size)?.available);
    }

    updatePriceDisplay() {
      const variants = this.getSelectedVariants().filter(v => v.available);

      if (variants.length === 0 || this.hasUnavailableSelection()) {
        if (this.priceEl) this.priceEl.textContent = '—';
        if (this.compareEl) this.compareEl.hidden = true;
        return;
      }

      const totalPrice = variants.reduce((sum, v) => sum + v.price, 0);
      const totalCompare = variants.reduce((sum, v) => {
        if (v.comparePrice && v.comparePrice > v.price) return sum + v.comparePrice;
        return sum + v.price;
      }, 0);

      if (this.priceEl) {
        this.priceEl.textContent = this.formatMoney(totalPrice);
      }

      if (this.compareEl) {
        if (totalCompare > totalPrice) {
          this.compareEl.textContent = this.formatMoney(totalCompare);
          this.compareEl.hidden = false;
        } else {
          this.compareEl.hidden = true;
        }
      }
    }

    updateConfirmButton() {
      if (!this.confirmBtn || this.confirmBtn.getAttribute('aria-busy') === 'true') return;

      const variants = this.getSelectedVariants().filter(v => v.available);
      const count = variants.length;

      this.confirmBtn.classList.remove('is-error');

      const selectedVariants = this.getSelectedVariants();
      const comingSoonOnly = count === 0 && selectedVariants.length > 0 && selectedVariants.every(v => this.isComingSoonVariant(v));

      if (comingSoonOnly) {
        this.confirmBtn.textContent = this.translate('comingSoon', 'Coming soon');
        this.confirmBtn.disabled = true;
        return;
      }

      if (this.hasUnavailableSelection()) {
        this.confirmBtn.textContent = this.translate('unavailableSelection', 'Remove unavailable sizes');
        this.confirmBtn.disabled = true;
        return;
      }

      if (count === 0) {
        this.confirmBtn.textContent = this.translate('selectSizes', 'Select sizes');
        this.confirmBtn.disabled = true;
        return;
      }

      this.confirmBtn.disabled = false;
      this.confirmBtn.textContent = count === 1
        ? this.translate('buildQuiver', 'Build Your Quiver')
        : this.withCount('buildQuiverCount', 'Build Your Quiver (__COUNT__)', count);
    }

    async confirmSelection() {
      const variants = this.getSelectedVariants().filter(v => v.available);
      if (this.hasUnavailableSelection()) {
        this.showToast(this.translate('unavailableSelection', 'Remove unavailable sizes'), 'error');
        return;
      }
      if (variants.length === 0) {
        this.showToast(this.translate('selectAvailableSize', 'Select at least one available size'), 'error');
        return;
      }

      await this.addToCart(variants);
    }

    getRoutesRoot() {
      if (typeof Shopify !== 'undefined' && Shopify.routes && Shopify.routes.root) {
        return Shopify.routes.root;
      }
      return '/';
    }

    getCartDrawerSectionId() {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) return null;

      if (cartDrawer.hasAttribute('section-id')) {
        return cartDrawer.getAttribute('section-id');
      }

      const sectionEl = cartDrawer.closest('.shopify-section, [id^="shopify-section-"]');
      if (sectionEl?.id) {
        return sectionEl.id.replace('shopify-section-', '');
      }

      return null;
    }

    prepareBundledSections() {
      const sectionsToBundle = new Set();

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        })
      );

      return sectionsToBundle;
    }

    buildCartItems(variants) {
      return variants.map(v => {
        const id = Number(v.variantId);
        if (!Number.isFinite(id) || id <= 0) {
          const error = new Error(`Invalid variant for size ${v.size}`);
          error.code = 'INVALID_VARIANT';
          throw error;
        }
        return { id, quantity: 1 };
      });
    }

    async requestCartAdd(routesRoot, items, sectionsToBundle) {
      const response = await fetch(`${routesRoot}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items,
          sections: [...sectionsToBundle].join(',')
        })
      });

      const responseText = await response.text();
      let responseJson = null;

      try {
        responseJson = responseText ? JSON.parse(responseText) : null;
      } catch (parseError) {
        throw new Error('Unexpected cart response from store');
      }

      if (!response.ok) {
        const error = new Error(this.translate('addError', 'Could not add to cart'));
        error.rejected = response.status >= 400 && response.status < 500 && response.status !== 422;
        throw error;
      }

      return responseJson;
    }

    async fetchCartDrawerSectionHtml(sectionId) {
      const routesRoot = this.getRoutesRoot();
      const response = await fetch(`${routesRoot}?section_id=${encodeURIComponent(sectionId)}`, {
        headers: { Accept: 'text/html' }
      });

      if (!response.ok) {
        throw new Error(`Could not refresh cart drawer (${response.status})`);
      }

      return response.text();
    }

    async ensureCartDrawerSections(cartContent, sectionId, addResponse) {
      cartContent.sections = cartContent.sections || {};

      if (addResponse?.sections) {
        Object.assign(cartContent.sections, addResponse.sections);
      }

      if (sectionId && !cartContent.sections[sectionId]) {
        try {
          cartContent.sections[sectionId] = await this.fetchCartDrawerSectionHtml(sectionId);
        } catch (error) {
          console.warn('LSCM: Cart drawer section fallback failed', error);
        }
      }
    }

    publishCartEvents(cartContent, addResponse, items) {
      document.documentElement.dispatchEvent(
        new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: addResponse?.items || items,
            cart: cartContent
          }
        })
      );

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            cart: cartContent
          }
        })
      );

      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.show === 'function' && !drawer.open) {
        drawer.show();
      }
    }

    showConfirmSuccess(count) {
      if (!this.confirmBtn) return;

      const original = this.confirmBtn.textContent;
      this.confirmBtn.textContent = count === 1
        ? this.translate('addedToCart', 'Added to cart!')
        : this.withCount('addedSails', 'Added __COUNT__ sails!', count);
      this.confirmBtn.classList.add('is-success');

      setTimeout(() => {
        if (!this.confirmBtn) return;
        this.confirmBtn.classList.remove('is-success');
        this.updateConfirmButton();
      }, 2000);
    }

    showConfirmError(message) {
      if (!this.confirmBtn) return;

      const original = this.updateConfirmButton.bind(this);
      this.confirmBtn.textContent = this.translate('addRetry', 'Could not add — try again');
      this.confirmBtn.classList.add('is-error');
      this.showToast(message, 'error');

      setTimeout(() => {
        if (!this.confirmBtn) return;
        this.confirmBtn.classList.remove('is-error');
        original();
      }, 2500);
    }

    async addToCart(variants) {
      if (!this.confirmBtn || this.confirmBtn.getAttribute('aria-busy') === 'true') return;

      this.confirmBtn.setAttribute('aria-busy', 'true');
      this.confirmBtn.classList.remove('is-error', 'is-success');
      this.confirmBtn.textContent = this.translate('adding', 'Adding...');

      const routesRoot = this.getRoutesRoot();

      let added = false;
      let items = [];
      this.confirmBtn.disabled = true;
      try {
        items = this.buildCartItems(variants);
        const sectionId = this.getCartDrawerSectionId();
        const sectionsToBundle = this.prepareBundledSections();
        const addResponse = await this.requestCartAdd(routesRoot, items, sectionsToBundle);
        added = true;

        const response = await fetch(`${routesRoot}cart.js`, {
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error('Cart refresh failed');
        const cartContent = await response.json();

        this.close();
        await this.ensureCartDrawerSections(cartContent, sectionId, addResponse);
        this.publishCartEvents(cartContent, addResponse, items);
        this.showConfirmSuccess(items.length);
      } catch (error) {
        if (error?.code === 'INVALID_VARIANT') {
          this.showConfirmError(this.translate('addError', 'Could not add to cart'));
        } else {
          console.error('LSCM: Cart operation failed', error);
          if (added || !error.rejected) {
            // A successful or ambiguous mutation must never be replayed. The
            // cart page reconciles the server state even if the drawer read fails.
            window.location.assign(`${routesRoot}cart`);
          } else {
            this.showConfirmError(this.translate('addError', 'Could not add to cart'));
          }
        }
      } finally {
        if (this.confirmBtn) {
          this.confirmBtn.removeAttribute('aria-busy');
          this.updateConfirmButton();
        }
      }
    }

    showToast(message, tone = 'info') {
      document.dispatchEvent(
        new CustomEvent('toast:show', {
          detail: { message, tone }
        })
      );
    }

    formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents);
      }
      return '€' + (cents / 100).toFixed(2).replace('.', ',');
    }

    formatConstruction(construction) {
      return construction || this.translate('standard', 'Standard');
    }

    formatColor(color) {
      return color || this.translate('default', 'Default');
    }
  }

  let instance = null;

  function init() {
    const root = document.querySelector('[data-lscm-root]');
    if (!root) return;
    if (instance?.root === root && instance.ready) return;

    if (instance?.root !== root) {
      instance?.destroy();
      instance = null;
    }

    const modal = new LoftsailsConfigureModal(root);
    if (modal.ready) {
      instance = modal;
    }
  }

  function openModal() {
    if (!instance?.ready) init();
    if (instance?.ready) {
      return instance.open();
    }
    console.warn('LSCM: Configure modal is not ready');
    return false;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('loftsails:product:updated', init);

  document.addEventListener('shopify:section:unload', (event) => {
    if (!instance?.root || !event.target?.contains?.(instance.root)) return;
    instance.destroy();
    instance = null;
  });

  document.addEventListener('lscm:open', openModal);

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-lscm-trigger]');
    if (!trigger) return;
    e.preventDefault();
    openModal();
  });

  window.LoftsailsConfigureModal = {
    open: openModal,
    getInstance: () => instance
  };
})();
